import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `settings:` scope (F10 — `/settings` shell,
 * Profile, Appearance, Notifications sections, plus the shared
 * `<SettingsNav>` used by every `/settings/*` page in this task), following
 * the exact `driveSettings:`/`web:` precedent (`apps/web/src/routes/
 * settings/drive/drive.i18n.ts`) — NOT a second i18n runtime (E18/§13.3
 * ban), just physically scoped here because `packages/i18n/**` is outside
 * this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/settings.json`.
 */
const en = {
  'nav.label': 'Settings',
  'nav.profile': 'Profile',
  'nav.billing': 'Plan & Billing',
  'nav.sessions': 'Sessions',
  // mockup-27 (design/app/27-settings.html): tab label is plain "Drive".
  'nav.drive': 'Drive',
  'nav.notifications': 'Notifications',
  'nav.referrals': 'Referrals',

  'page.eyebrow': 'Account',
  'page.title': 'Settings',

  // --- Profile section ---
  'profile.title': 'Profile',
  'profile.status.loading': 'Loading your profile…',
  'profile.status.error.title': "Couldn't load your profile.",
  'profile.avatar.upload': 'Upload photo',
  'profile.avatar.uploading': 'Uploading…',
  'profile.avatar.processing': 'Processing your photo — this can take a moment.',
  'profile.avatar.remove': 'Remove photo',
  'profile.avatar.error': "Couldn't upload that photo. Try a smaller image.",
  'profile.displayName.label': 'Display name',
  'profile.email.label': 'Email',
  'profile.email.verified': 'Verified',
  'profile.email.unverified': 'Unverified',
  'profile.authMethods.label': 'Sign-in methods',
  'profile.authMethods.password': 'Password',
  'profile.authMethods.google': 'Google',
  'profile.authMethods.magic_link': 'Magic link',
  'profile.action.save': 'Save changes',
  'profile.toast.saved.action': 'Saved',
  'profile.toast.saveFailed': "Couldn't save your profile. Try again.",

  // --- Appearance section ---
  'appearance.title': 'Appearance',
  'appearance.theme.label': 'Theme',
  'appearance.theme.system': 'System',
  'appearance.theme.light': 'Light',
  'appearance.theme.dark': 'Dark',
  'appearance.language.label': 'Language',

  // --- Notifications section ---
  'notifications.title': 'Notifications',
  'notifications.description': 'Choose how you want to hear about activity on your account.',
  'notifications.devNotice': 'Dev note: no read endpoint exists yet for saved preferences — these toggles start from defaults every visit, not your last save.',
  'notifications.column.inApp': 'In-app',
  'notifications.column.webPush': 'Push',
  'notifications.column.email': 'Email',
  'notifications.type.job.done': 'A generation finishes',
  'notifications.type.comment.new': 'Someone comments on your share',
  'notifications.type.reaction.new': 'Someone reacts to your share',
  'notifications.type.share.approved': 'Your share is approved',
  'notifications.type.credit.low': 'Your credit balance is low',
  'notifications.action.save': 'Save preferences',
  'notifications.toast.saved.action': 'Saved',
  'notifications.toast.saveFailed': "Couldn't save your notification preferences. Try again.",

  // --- Danger Zone section (F10 — no FEATURES.md spec, designed fresh;
  // see apps/server/src/account/handlers.ts's top-of-file comment) ---
  'dangerZone.title': 'Danger zone',
  'dangerZone.delete.title': 'Delete account',
  'dangerZone.delete.description': 'Removes your documents, shares & data.',
  'dangerZone.delete.cta': 'Delete…',
  'dangerZone.delete.modal.title': 'Delete your account',
  'dangerZone.delete.modal.description':
    'This permanently deletes your account, documents, and shares. This cannot be undone.',
  'dangerZone.delete.modal.nameLabel': 'Type your name ({name}) to confirm',
  'dangerZone.delete.modal.emailLabel': 'Type your email ({email}) to confirm',
  'dangerZone.delete.modal.confirmCta': 'Delete my account',
  'dangerZone.delete.toast.failed': "Couldn't delete your account. Try again.",
  'dangerZone.farewell.title': 'Your account has been deleted',
  'dangerZone.farewell.description': "We're sorry to see you go. Signing you out…",
  'dangerZone.export.title': 'Export my data',
  'dangerZone.export.description': 'Download a copy of your documents, shares, and account data.',
  'dangerZone.export.cta': 'Export my data',
  'dangerZone.export.toast.started': "Export started — we'll notify you with a download link when it's ready.",
  'dangerZone.export.toast.failed': "Couldn't start your export. Try again.",

  'action.retry': 'Retry',
} as const;

const ro: Record<keyof typeof en, string> = {
  'nav.label': 'Setări',
  'nav.profile': 'Profil',
  'nav.billing': 'Plan și facturare',
  'nav.sessions': 'Sesiuni',
  // mockup-27 (design/app/27-settings.html): tab label is plain "Drive".
  'nav.drive': 'Drive',
  'nav.notifications': 'Notificări',
  'nav.referrals': 'Recomandări',

  'page.eyebrow': 'Cont',
  'page.title': 'Setări',

  'profile.title': 'Profil',
  'profile.status.loading': 'Se încarcă profilul tău…',
  'profile.status.error.title': 'Nu am putut încărca profilul tău.',
  'profile.avatar.upload': 'Încarcă o fotografie',
  'profile.avatar.uploading': 'Se încarcă…',
  'profile.avatar.processing': 'Se procesează fotografia — poate dura puțin.',
  'profile.avatar.remove': 'Elimină fotografia',
  'profile.avatar.error': 'Nu am putut încărca acea fotografie. Încearcă o imagine mai mică.',
  'profile.displayName.label': 'Nume afișat',
  'profile.email.label': 'Email',
  'profile.email.verified': 'Verificat',
  'profile.email.unverified': 'Neverificat',
  'profile.authMethods.label': 'Metode de autentificare',
  'profile.authMethods.password': 'Parolă',
  'profile.authMethods.google': 'Google',
  'profile.authMethods.magic_link': 'Link magic',
  'profile.action.save': 'Salvează modificările',
  'profile.toast.saved.action': 'Salvat',
  'profile.toast.saveFailed': 'Nu am putut salva profilul tău. Încearcă din nou.',

  'appearance.title': 'Aspect',
  'appearance.theme.label': 'Temă',
  'appearance.theme.system': 'Sistem',
  'appearance.theme.light': 'Deschis',
  'appearance.theme.dark': 'Închis',
  'appearance.language.label': 'Limbă',

  'notifications.title': 'Notificări',
  'notifications.description': 'Alege cum vrei să afli despre activitatea din contul tău.',
  'notifications.devNotice': 'Notă dev: nu există încă un endpoint de citire pentru preferințele salvate — aceste comutatoare pornesc din valori implicite la fiecare vizită, nu din ultima salvare.',
  'notifications.column.inApp': 'În aplicație',
  'notifications.column.webPush': 'Push',
  'notifications.column.email': 'Email',
  'notifications.type.job.done': 'O generare se finalizează',
  'notifications.type.comment.new': 'Cineva comentează la partajarea ta',
  'notifications.type.reaction.new': 'Cineva reacționează la partajarea ta',
  'notifications.type.share.approved': 'Partajarea ta este aprobată',
  'notifications.type.credit.low': 'Soldul tău de credite este scăzut',
  'notifications.action.save': 'Salvează preferințele',
  'notifications.toast.saved.action': 'Salvat',
  'notifications.toast.saveFailed': 'Nu am putut salva preferințele de notificare. Încearcă din nou.',

  'dangerZone.title': 'Zonă periculoasă',
  'dangerZone.delete.title': 'Șterge contul',
  'dangerZone.delete.description': 'Elimină documentele, distribuirile și datele tale.',
  'dangerZone.delete.cta': 'Șterge…',
  'dangerZone.delete.modal.title': 'Șterge-ți contul',
  'dangerZone.delete.modal.description':
    'Aceasta șterge definitiv contul, documentele și distribuirile tale. Nu poate fi anulată.',
  'dangerZone.delete.modal.nameLabel': 'Scrie numele tău ({name}) pentru a confirma',
  'dangerZone.delete.modal.emailLabel': 'Scrie emailul tău ({email}) pentru a confirma',
  'dangerZone.delete.modal.confirmCta': 'Șterge-mi contul',
  'dangerZone.delete.toast.failed': 'Nu am putut șterge contul tău. Încearcă din nou.',
  'dangerZone.farewell.title': 'Contul tău a fost șters',
  'dangerZone.farewell.description': 'Ne pare rău să te vedem plecând. Te delogăm…',
  'dangerZone.export.title': 'Exportă datele mele',
  'dangerZone.export.description': 'Descarcă o copie a documentelor, distribuirilor și datelor contului tău.',
  'dangerZone.export.cta': 'Exportă datele mele',
  'dangerZone.export.toast.started': 'Exportul a început — te vom anunța cu un link de descărcare când este gata.',
  'dangerZone.export.toast.failed': 'Nu am putut porni exportul tău. Încearcă din nou.',

  'action.retry': 'Reîncearcă',
};

export const SETTINGS_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type SettingsCatalogKey = keyof typeof en;
